import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getAllUsers, getPendingUnsuspensionRequests } from '../../../api/services/adminService';

import UserDetailsModal from './UserDetailsModal';
import './UserSearch.css';

import UserAvatar from './UserAvatar';

const UserSearch = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all'); // 'all', 'LANDLORD', 'GUEST', 'AGENT', 'BNB_HOST', 'ADMIN'
  const [viewMode, setViewMode] = useState('users'); // 'users' or 'unsuspension'
  const [unsuspensionRequests, setUnsuspensionRequests] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUnsuspensionRequestId, setSelectedUnsuspensionRequestId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Load users from API
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllUsers(page, size);
      
      if (response.status === 'success' && response.data) {
        setUsers(response.data.content || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalElements(response.data.totalElements || 0);
      }
    } catch (err) {
      setError(err.message || 'Failed to load users');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  }, [page, size]);

  // Load unsuspension requests from API
  const loadUnsuspensionRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPendingUnsuspensionRequests(page, size);
      
      if (response.status === 'success' && response.data) {
        setUnsuspensionRequests(response.data.content || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalElements(response.data.totalElements || 0);
      }
    } catch (err) {
      setError(err.message || 'Failed to load unsuspension requests');
      console.error('Error loading unsuspension requests:', err);
    } finally {
      setLoading(false);
    }
  }, [page, size]);

  useEffect(() => {
    if (viewMode === 'unsuspension') {
      loadUnsuspensionRequests();
    } else {
      loadUsers();
    }
  }, [viewMode, loadUsers, loadUnsuspensionRequests]);

  // Filter users based on search query and role filter
  const filteredUsers = useMemo(() => {
    let filtered = users;

    // Filter by role
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => 
        user.roles && user.roles.some(role => role === roleFilter)
      );
    }

    // Filter by search query (search in name, email, phone, id)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(user => 
        `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        user.phone?.toLowerCase().includes(query) ||
        user.id?.toLowerCase().includes(query) ||
        user.idNumber?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [users, searchQuery, roleFilter]);

  // Filter unsuspension requests based on search query
  const filteredUnsuspensionRequests = useMemo(() => {
    let filtered = unsuspensionRequests;

    // Filter by search query (search in name, email)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(request => 
        request.userName?.toLowerCase().includes(query) ||
        request.userEmail?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [unsuspensionRequests, searchQuery]);

  // Helper functions for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'N/A';
    }
  };

  const getUserRoles = (roles) => {
    if (!roles || roles.length === 0) return 'N/A';
    return roles.join(', ');
  };

  const getStatusBadge = (isActive) => {
    return (
      <span className={`status-badge ${isActive ? 'active' : 'inactive'}`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  const getVerificationBadge = (status) => {
    if (!status || status === 'NONE') return <span className="badge-none">Not Verified</span>;
    if (status === 'APPROVED') return <span className="badge-approved">Verified</span>;
    return <span className="badge-pending">Pending</span>;
  };

  const handleViewDetails = (userId, unsuspensionRequestId = null) => {
    setSelectedUserId(userId);
    setSelectedUnsuspensionRequestId(unsuspensionRequestId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUserId(null);
    setSelectedUnsuspensionRequestId(null);
  };

  return (
    <div className="user-search-container">
      <div className="user-search-header">
        <div>
          <h1 className="page-title">User Management</h1>
          <p className="page-subtitle">
            {viewMode === 'unsuspension' 
              ? 'View and manage pending unsuspension requests' 
              : 'View and search all users in the system'}
          </p>
        </div>
        <div className="user-count">
          {viewMode === 'unsuspension' 
            ? `Total Requests: ${totalElements}` 
            : `Total Users: ${totalElements}`}
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="search-section">
        <div className="search-controls">
          <div className="search-input-group">
            <label htmlFor="view-mode-select" className="search-label">View:</label>
            <select
              id="view-mode-select"
              className="search-select"
              value={viewMode}
              onChange={(e) => {
                setViewMode(e.target.value);
                setPage(0); // Reset to first page when changing view
                setSearchQuery(''); // Clear search
                setRoleFilter('all'); // Reset role filter
              }}
            >
              <option value="users">All Users</option>
              <option value="unsuspension">Pending Unsuspension</option>
            </select>
          </div>

          {viewMode === 'users' && (
            <div className="search-input-group">
              <label htmlFor="role-filter" className="search-label">Filter by Role:</label>
              <select
                id="role-filter"
                className="search-select"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="all">All Roles</option>
                <option value="LANDLORD">Landlord</option>
                <option value="GUEST">Guest</option>
                <option value="AGENT">Agent</option>
                <option value="BNB_HOST">BNB Host</option>
                <option value="ADMIN">Admin</option>
              </select>
            </div>
          )}
          
          <div className="search-input-group search-input-expanded">
            <label htmlFor="search-input" className="search-label">Search:</label>
            <input
              id="search-input"
              type="text"
              className="search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={viewMode === 'unsuspension' ? 'Search by name or email...' : 'Search by name, email, phone, or ID...'}
            />
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <p>Loading users...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="error-state">
          <p>Error: {error}</p>
          <button onClick={loadUsers} className="retry-button">Retry</button>
        </div>
      )}

      {/* Users Table */}
      {!loading && !error && viewMode === 'users' && (
        <>
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Photo</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Roles</th>
                  <th>Status</th>
                  <th>KYC Status</th>
                  <th>KYB Status</th>
                  <th>Last Login</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="no-results">
                      {searchQuery || roleFilter !== 'all' 
                        ? 'No users found matching your criteria' 
                        : 'No users found'}
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <UserAvatar 
                          profilePhotoPath={user.profilePhotoPath}
                          firstName={user.firstName}
                          lastName={user.lastName}
                          userId={user.id}
                        />
                      </td>
                      <td>
                        <div className="user-name-cell">
                          <span>{user.firstName} {user.lastName}</span>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>{user.phone || 'N/A'}</td>
                      <td>
                        <div className="roles-cell">
                          {getUserRoles(user.roles)}
                        </div>
                      </td>
                      <td>{getStatusBadge(user.isActive)}</td>
                      <td>{getVerificationBadge(user.kycStatus)}</td>
                      <td>{getVerificationBadge(user.kybStatus)}</td>
                      <td>{formatDate(user.lastLogin)}</td>
                      <td>{formatDate(user.createdAt)}</td>
                      <td>
                        <button 
                          className="btn-view"
                          onClick={() => handleViewDetails(user.id)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="pagination-btn"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {page + 1} of {totalPages} ({totalElements} total {viewMode === 'unsuspension' ? 'requests' : 'users'})
              </span>
              <button 
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Unsuspension Requests Table */}
      {!loading && !error && viewMode === 'unsuspension' && (
        <>
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>User Name</th>
                  <th>Email</th>
                  <th>Reason</th>
                  <th>Request Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUnsuspensionRequests.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="no-results">
                      {searchQuery 
                        ? 'No unsuspension requests found matching your criteria' 
                        : 'No pending unsuspension requests'}
                    </td>
                  </tr>
                ) : (
                  filteredUnsuspensionRequests.map((request) => (
                    <tr key={request.id}>
                      <td>
                        <div className="user-name-cell">
                          <span>{request.userName || 'N/A'}</span>
                        </div>
                      </td>
                      <td>{request.userEmail}</td>
                      <td>
                        <div className="reason-cell">
                          {request.reason || 'No reason provided'}
                        </div>
                      </td>
                      <td>{formatDate(request.createdAt)}</td>
                      <td>
                        <span className="badge-pending">{request.status}</span>
                      </td>
                      <td>
                        <button 
                          className="btn-view"
                          onClick={() => handleViewDetails(request.userId, request.id)}
                        >
                          View User
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination for Unsuspension Requests */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="pagination-btn"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {page + 1} of {totalPages} ({totalElements} total {totalElements === 1 ? 'request' : 'requests'})
              </span>
              <button 
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* User Details Modal */}
      <UserDetailsModal
        userId={selectedUserId}
        unsuspensionRequestId={selectedUnsuspensionRequestId}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default UserSearch;
